import { redirect } from 'react-router';
import { tokenStore } from '@/lib/http-core';
import { bootstrapAuth } from '@/lib/apiClient';
import type { AdminRole } from '@/lib/api-admin';

interface JwtPayload {
  roles?: readonly string[];
  twoFactorVerified?: boolean;
  exp?: number;
}

const decodeBase64Url = (segment: string): string => {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
  const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  if (typeof atob === 'function') return atob(padded + pad);
  return Buffer.from(padded + pad, 'base64').toString('binary');
};

export const parseJwtRoles = (
  token: string | null,
): { roles: readonly AdminRole[]; twoFactorVerified: boolean } | null => {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const json = decodeBase64Url(parts[1] ?? '');
    const decoded = JSON.parse(json) as JwtPayload;
    const roles = Array.isArray(decoded.roles)
      ? (decoded.roles.filter((r): r is AdminRole => typeof r === 'string') as readonly AdminRole[])
      : [];
    return { roles, twoFactorVerified: decoded.twoFactorVerified === true };
  } catch {
    return null;
  }
};

/**
 * Cold-start 복구: 메모리 토큰이 없으면 부팅 1회 silent refresh 를 await 한 뒤에만
 * /login 으로 보낸다. 새 탭/sessionStorage 소실 상황에서 유효한 refresh 쿠키로 세션을
 * 되살린다. 정상 네비게이션(토큰 보유)에선 bootstrapAuth 가 즉시 resolve 하므로 비용 0.
 * 진짜 익명 방문자는 refresh 가 null → /login (의도된 동작).
 */
const ensureSession = async (): Promise<string | null> => {
  if (tokenStore.token) return tokenStore.token;
  await bootstrapAuth();
  return tokenStore.token;
};

export const authGuard = async (): Promise<null> => {
  const token = await ensureSession();
  if (!token) throw redirect('/login');
  return null;
};

export const rbacLoader =
  (required: ReadonlyArray<AdminRole>) =>
  async (): Promise<null> => {
    const token = await ensureSession();
    if (!token) throw redirect('/login');
    const claims = parseJwtRoles(token);
    if (!claims) throw redirect('/login');
    const hasRole = required.some((r) => claims.roles.includes(r));
    if (!hasRole) throw redirect('/dashboard?denied=1');
    return null;
  };
